import { defineDocument } from '../../schema';
export const featureGameRoomLifecycleDocument = defineDocument({
    id: 'feature:game-room-lifecycle',
    kind: 'feature',
    slug: 'game-room-lifecycle',
    route: '/discover/features/game-room-lifecycle',
    seo: {
        title: 'Discover Game Room Lifecycle',
        description: 'Create, join, start, and close game rooms with room codes, player customization, and host controls.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Feature',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Game Room Lifecycle',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Create, join, start, and close game rooms with room codes, player customization, and host controls.',
            },
        ],
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'creating-a-room',
            title: [
                {
                    type: 'text',
                    text: 'Creating a Room',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Create a room by hovering over a game in the ',
                        },
                        {
                            type: 'link',
                            href: '/library',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Game Library',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' and pressing the ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Select game',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' button. This opens the game room lobby with a unique 6-digit room code. Only the host needs to create the room. Hosts must be ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/login',
                            children: [
                                {
                                    type: 'text',
                                    text: 'logged in',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' and have ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/loons#using-loons',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Loons',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' to spend. Once created, the lobby opens immediately so players can start joining.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Rooms are created using the host's current ",
                        },
                        {
                            type: 'link',
                            href: '/discover/features/user-preferences',
                            children: [
                                {
                                    type: 'text',
                                    text: 'User Preferences',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' and ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/moderator-tools',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Moderator Tools',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' from their user profile. To change the defaults for future rooms, update your preferences and moderator tools before creating a new room. Each room keeps the preferences and moderator tools it was created with.',
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featureSelectGame',
                    imageAlt: 'Game Library selection modal with a Select game button.',
                    caption: [
                        {
                            type: 'text',
                            text: 'Choose a game from the library and press ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Select game',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' to create a room.',
                        },
                    ],
                    maxWidth: 800,
                },
            ],
        },
        {
            id: 'joining-a-room',
            title: [
                {
                    type: 'text',
                    text: 'Joining a Room',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players can join from any browser-enabled device, including desktops, tablets, or mobile phones. There are three ways to join a room:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Scan the QR code shown on the host screen.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Go directly to ',
                            },
                            {
                                type: 'link',
                                href: '/join',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Join',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' and enter the 6-digit room code.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Tap the link shared by the host to land on Join with the room code pre-filled.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "After entering the room, players add a name and customize their avatar's game face with a color and facial expressions. Player names are limited to 12 characters with basic validation rules to prevent trolling. Player names and avatar colors must always be unique to the room.",
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featurePlayerStandard',
                    imageAlt: 'Player join screen with room code and avatar customization.',
                    caption: [
                        {
                            type: 'text',
                            text: 'Join on any device and customize your game face.',
                        },
                    ],
                    maxWidth: 400,
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Users have Moderator Tools available to them to enforce how players can join their game rooms. When ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/moderator-tools#player-access__require-twitch-login',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Require Twitch Login',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' is enabled by the host, players must authorize with Twitch before joining the room. Hosts can also enable ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/moderator-tools#player-access__disallow-blocked-banned',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Disallow Blocked & Banned',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' to prevent anyone on their Twitch blocked or banned lists from joining. Hosts can also enable ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/moderator-tools#censorship',
                            children: [
                                {
                                    type: 'text',
                                    text: 'profanity filters',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' to block profane player names during the join process.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'When ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Confirm player',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' is pressed, the player joins the lobby and sees a ',
                        },
                        {
                            type: 'em',
                            children: [
                                {
                                    type: 'text',
                                    text: '"Waiting for game to start..."',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' message on their screen. Players can return to the previous screen to update their avatar details anytime before the host starts the game.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Player sessions last up to three days. During an active session, a player's preferences will be pre-populated each time the player returns to join a new room. When a player joins with Twitch, the player's avatar will always use their last known preferences regardless of session.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'It is recommended for the host to join as a participant from a different browser window or device than the one they are hosting on.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'starting-a-game',
            title: [
                {
                    type: 'text',
                    text: 'Starting a Game',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Each game lists a minimum and maximum player count in the ',
                        },
                        {
                            type: 'link',
                            href: '/library',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Game Library',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: '. The host cannot start a game until the minimum is reached. If the room has not met the minimum count yet, the host button reads ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Waiting for players',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: '. Once the minimum is reached, it changes to ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Start game',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: '.',
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featureRoomStart',
                    imageAlt: 'Host lobby view showing the Start game button and player list.',
                    caption: [
                        {
                            type: 'text',
                            text: 'Hosts start the game after the minimum player count is reached.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'After pressing ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Start game',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ', the ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/loons#using-loons',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Loon cost',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' is deducted from your balance, the host screen transitions into the game, and the instructions begin. Players will see a ',
                        },
                        {
                            type: 'em',
                            children: [
                                {
                                    type: 'text',
                                    text: '"Starting the game..."',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' message until the instructions are finished. All games include a ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Skip intro',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' button underneath Poppy while the game instructions are playing.',
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featureSkipIntro',
                    imageAlt: 'Game intro screen with a Skip intro option.',
                    caption: [
                        {
                            type: 'text',
                            text: 'Skip intro button, as seen in Quinspire.',
                        },
                    ],
                    maxWidth: 400,
                },
            ],
        },
        {
            id: 'game-in-progress',
            title: [
                {
                    type: 'text',
                    text: 'Game In Progress',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Once a game is in progress, player details and room configuration are locked and cannot be changed. The room lobby and player join screens show a ',
                        },
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Go to game',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' button in case a participant is accidentally navigated away from the game.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'If the game appears stuck for an extended period of time (as a player or a host), first try refreshing your browser to re-sync your device with the game state on the server. For game interruptions that persist or impact multiple players, please contact support via ',
                        },
                        {
                            type: 'link',
                            href: 'mailto:poppy@airheadgames.com',
                            children: [
                                {
                                    type: 'text',
                                    text: 'email',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' or ',
                        },
                        {
                            type: 'link',
                            href: 'https://discord.gg/rfFTVAqHwH',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Discord',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: '.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'closing-a-room',
            title: [
                {
                    type: 'text',
                    text: 'Closing a Room',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Rooms that are idle for more than 30 minutes are considered abandoned and will close automatically. More importantly, there are a few ways for hosts to close a room:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'At any time, open ',
                            },
                            {
                                type: 'link',
                                href: '/discover/features/poppy#in-game-menu',
                                children: [
                                    {
                                        type: 'text',
                                        text: "Poppy's Menu",
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' and choose ',
                            },
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'End game',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' to close the room for all players.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'From the lobby, press the ',
                            },
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'back arrow',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' next to the game name in the top left corner of the screen.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'At the end of a game, click the X icon and choose ',
                            },
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Go to game library',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: '.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'When a room closes, it becomes unavailable/unjoinable and all players are removed from the room regardless of the game state at the time of closure. Closing a room returns hosts to the ',
                        },
                        {
                            type: 'link',
                            href: '/library',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Game Library',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' and players to the ',
                        },
                        {
                            type: 'link',
                            href: '/join',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Join',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' screen in preparation for the next game.',
                        },
                    ],
                },
            ],
        },
    ],
});
//# sourceMappingURL=game-room-lifecycle.js.map